import { Box, Stack, styled } from '@mui/material'
import { CheckCircleIcon, ClockIcon, XCircleIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { ContentChip } from 'ComponentLibrary/src/piazza/content'
import { type ReactNode, useEffect, useState } from 'react'

type AiImportSectionTiming = { startedAt: number; completedAt?: number }

type PdfExtractionStepsCopy = {
    title: string
    description: string
    retrying: string
    formatElapsedSeconds: (seconds: number) => string
    failedSectionAriaLabel: string
}

const DEFAULT_EXTRACTION_STEPS_COPY: PdfExtractionStepsCopy = {
    title: 'Ausgelesene Werte',
    description: 'Bitte alle KI-extrahierten Werte prüfen.',
    retrying: 'Erneuter Versuch',
    formatElapsedSeconds: (seconds) => `${seconds} s`,
    failedSectionAriaLabel: 'Abschnitt konnte nicht ausgelesen werden',
}

export function PdfExtractionSteps({
    sectionLabels,
    activeSectionIndexes,
    completedSectionIndexes,
    failedSectionIndexes,
    retryingSectionIndexes,
    sectionTimings,
    headerStatus,
    copy = DEFAULT_EXTRACTION_STEPS_COPY,
}: {
    sectionLabels: readonly string[]
    activeSectionIndexes: number[]
    completedSectionIndexes: number[]
    failedSectionIndexes: number[]
    retryingSectionIndexes: number[]
    sectionTimings: Partial<Record<number, AiImportSectionTiming>>
    headerStatus?: ReactNode
    copy?: PdfExtractionStepsCopy
}) {
    const [now, setNow] = useState(() => Date.now())
    const hasRunningSection = Object.values(sectionTimings).some((timing) => timing?.completedAt === undefined)

    useEffect(() => {
        if (!hasRunningSection) {
            return
        }
        const interval = window.setInterval(() => setNow(Date.now()), 1000)
        return () => window.clearInterval(interval)
    }, [hasRunningSection])

    return (
        <ExtractionStepsCard>
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap={tokens.SizingSpacing150}>
                <Stack gap={tokens.SizingSpacing025}>
                    <Typography variant="headline4" color={tokens.ColorTextBoldest}>
                        {copy.title}
                    </Typography>
                    <Typography variant="textXS" color={tokens.ColorTextDefault}>
                        {copy.description}
                    </Typography>
                </Stack>
                {headerStatus ?? (
                    <Typography variant="textXS" color={tokens.ColorTextSubtle}>
                        {completedSectionIndexes.length}/{sectionLabels.length}
                    </Typography>
                )}
            </Stack>
            <Stack gap={tokens.SizingSpacing050}>
                {sectionLabels.map((sectionLabel, index) => (
                    <ExtractionStepRow
                        key={sectionLabel}
                        status={getExtractionStepStatus(index, activeSectionIndexes, completedSectionIndexes, failedSectionIndexes)}
                        title={sectionLabel}
                        elapsedSeconds={getElapsedSeconds(sectionTimings[index], now)}
                        isRetrying={retryingSectionIndexes.includes(index)}
                        copy={copy}
                    />
                ))}
            </Stack>
        </ExtractionStepsCard>
    )
}

function ExtractionStepRow({
    status,
    title,
    elapsedSeconds,
    isRetrying,
    copy,
}: {
    status: ExtractionStepStatus
    title: string
    elapsedSeconds?: number
    isRetrying: boolean
    copy: PdfExtractionStepsCopy
}) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            gap={tokens.SizingSpacing100}
            padding={`${tokens.SizingSpacing075} ${tokens.SizingSpacing100}`}
        >
            <Box display="inline-flex" flexShrink={0}>
                <ExtractionStepStatusIcon status={status} failedSectionAriaLabel={copy.failedSectionAriaLabel} />
            </Box>
            <Typography variant="textS" color={tokens.ColorTextBold}>
                {title}
            </Typography>
            <Stack direction="row" alignItems="center" gap={tokens.SizingSpacing100} marginLeft="auto">
                {isRetrying && <ContentChip size="small" color="orange" label={copy.retrying} />}
                {elapsedSeconds !== undefined && (
                    <ElapsedTime variant="textXS" color={tokens.ColorTextSubtle}>
                        {copy.formatElapsedSeconds(elapsedSeconds)}
                    </ElapsedTime>
                )}
            </Stack>
        </Stack>
    )
}

type ExtractionStepStatus = 'inactive' | 'running' | 'completed' | 'failed'

function ExtractionStepStatusIcon({ status, failedSectionAriaLabel }: { status: ExtractionStepStatus; failedSectionAriaLabel: string }) {
    if (status === 'failed') {
        return <XCircleIcon aria-label={failedSectionAriaLabel} size={tokens.SizingIconS} color={tokens.ColorIconDanger} weight="fill" />
    }

    if (status === 'completed') {
        return <CheckCircleIcon size={tokens.SizingIconS} color={tokens.ColorIconPositive} weight="fill" />
    }

    if (status === 'running') {
        return <RunningStepIcon />
    }

    return <ClockIcon size={tokens.SizingIconS} color={tokens.ColorIconDisabled} weight="bold" />
}

function getExtractionStepStatus(
    index: number,
    activeSectionIndexes: number[],
    completedSectionIndexes: number[],
    failedSectionIndexes: number[]
): ExtractionStepStatus {
    if (failedSectionIndexes.includes(index)) {
        return 'failed'
    }
    if (completedSectionIndexes.includes(index)) {
        return 'completed'
    }
    if (activeSectionIndexes.includes(index)) {
        return 'running'
    }
    return 'inactive'
}

const getElapsedSeconds = (timing: AiImportSectionTiming | undefined, now: number): number | undefined =>
    timing ? Math.floor(Math.max(0, (timing.completedAt ?? now) - timing.startedAt) / 1000) : undefined

const ElapsedTime = styled(Typography)(() => ({
    fontVariantNumeric: 'tabular-nums',
}))

const ExtractionStepsCard = styled(Stack)(() => ({
    width: '100%',
    gap: tokens.SizingSpacing150,
    padding: tokens.SizingSpacing200,
    border: `${tokens.SizingBorderS} solid ${tokens.ColorBorderDefault}`,
    borderRadius: tokens.SizingCornerM,
    backgroundColor: tokens.ColorSurfaceDefault,
}))

const RunningStepIcon = styled(Box)(() => ({
    width: tokens.SizingIconS,
    height: tokens.SizingIconS,
    borderRadius: '50%',
    border: `2px solid ${tokens.ColorIconBoldest}`,
    borderTopColor: 'transparent',
    animation: 'import-step-spin 1s linear infinite',
    '@keyframes import-step-spin': {
        from: { transform: 'rotate(0deg)' },
        to: { transform: 'rotate(360deg)' },
    },
}))
